import { CustomField } from "@measured/puck";
import { Select, MenuItem, FormControl, Box, CircularProgress, Typography, useTheme } from "@mui/material";

import { usePuckCompanyScore } from "../contexts/PuckCompanyScoreContext";
import { withContexts } from "../wrappers/withContexts";
import { PuckData } from "../contexts/PuckContext";
import { NativePuckTitleField } from "./NativePuckTitleField";
import { t } from "i18next";

type VariationData = {
    label?: string;
    score?: number;
};

type VariationItem = {
    id: number | string;
    label?: string;
    score?: number;
};

type VariationSelectFieldProps = {
    field: CustomField<string | null | undefined>;
    name: string;
    id: string;
    value: string | null | undefined;
    scoreId?: number;
    data?: PuckData;
    onChange: (value: string | null) => void;
    readOnly?: boolean;
    [key: string]: unknown;
};

const PUCK_CONFIG = {
    FontFamily: "var(--puck-font-family) !important",
};

export const VariationSelectField = (props: VariationSelectFieldProps) => {
    const { value, onChange, scoreId, data } = props;
    const theme = useTheme();
    const { companyPost, isLoading } = usePuckCompanyScore();

    const puckSelectStyles = {
        fontFamily: PUCK_CONFIG.FontFamily,
        fontSize: "0.875rem",
        "& .MuiSelect-select": {
            padding: theme.spacing(1.5, 2),
        },
        "& .MuiOutlinedInput-notchedOutline": {
            border: `1px solid ${theme.custom.grey[700]} !important`,
        },
    };

    const scoreIdValue =
        scoreId ? Number(props.scoreId)
        : data?.scoreId ? Number(data.scoreId)
        : undefined;

    let variations: Record<string, VariationData> = {};

    if (scoreIdValue && companyPost?.scores?.[scoreIdValue]?.variations) {
        const rawVariations = companyPost.scores[scoreIdValue].variations;

        if (rawVariations && typeof rawVariations === "object") {
            if (Array.isArray(rawVariations)) {
                rawVariations.forEach((item: unknown) => {
                    const variationItem = item as VariationItem;
                    if (variationItem && variationItem.id) {
                        variations[variationItem.id.toString()] = {
                            label: variationItem.label,
                            score: variationItem.score,
                        };
                    }
                });
            } else {
                variations = rawVariations as Record<string, VariationData>;
            }
        }
    }

    const getVariationEntries = () => {
        if (!scoreIdValue || !companyPost?.scores?.[scoreIdValue]?.variations) {
            return [];
        }
        return Object.entries(variations);
    };

    if (isLoading) {
        return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">{t("loading.variations", "Loading variations...")}</Typography>
            </Box>
        );
    }

    if (!scoreIdValue) {
        return (
            <Typography variant="body2" color="text.secondary">
                {t("variations.selectScore", "Please select a score first")}
            </Typography>
        );
    }

    const variationEntries = getVariationEntries();
    if (variationEntries.length === 0) {
        return <></>;
    }

    return (
        <FormControl fullWidth>
            <NativePuckTitleField label={t("variation.title", "Variation")} icon="plus" />
            <Select
                labelId="variation-select-label"
                id="variation-select"
                value={value || ""}
                displayEmpty
                onChange={(e) => {
                    onChange(e.target.value === "" ? null : e.target.value);
                }}
                sx={puckSelectStyles}
            >
                <MenuItem value="" sx={{ ...puckSelectStyles, borderRadius: 0 }}>
                    <span>{t("variation.none", "None (Show main score)")}</span>
                </MenuItem>
                {variationEntries.map(([key, variation]) => (
                    <MenuItem key={key} value={key} sx={{ ...puckSelectStyles, borderRadius: 0 }}>
                        {variation.label || key}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

export default withContexts(VariationSelectField);
